import { Box, Button, Typography, Paper, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface StickyCTAProps {
  label: string;
  price?: number;
  onClick: () => void;
  disabled: boolean;
  loading?: boolean;
  progress?: number; // Progress percentage (0-100)
  completedSteps?: number; // e.g., "2 of 4" — kept for backward compatibility
  totalSteps?: number; // kept for backward compatibility
  statusText?: string; // Takes priority over "X of 3 Complete" counter
  hint?: string; // Shown above button when disabled
  dotCount?: number; // Number of dots for mobile progress indicator
  dotCompleted?: number; // Number of completed dots
  onBack?: () => void; // Back button handler
  showBack?: boolean; // Whether to show back button
}

export function StickyCTA({
  label,
  price,
  onClick,
  disabled,
  loading,
  progress = 0,
  completedSteps,
  totalSteps,
  statusText,
  hint,
  dotCount,
  dotCompleted,
  onBack,
  showBack = false,
}: StickyCTAProps) {
  const theme = useTheme();
  // No animation - display price directly
  const displayPrice = price || 0;

  return (
    <Paper
      elevation={12}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        borderRadius: 0,
        borderTop: '1px solid',
        borderColor: 'divider',
        background: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(10px)',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
      }}
    >
      {/* Progress bar */}
      {(progress > 0 || (completedSteps !== undefined && totalSteps !== undefined)) && (
        <LinearProgress
          variant="determinate"
          value={progress || ((completedSteps || 0) / (totalSteps || 1)) * 100}
          sx={{
            height: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #6366F1 0%, #EC4899 100%)',
              transition: 'transform 0.3s ease',
            },
          }}
        />
      )}

      <Box
        sx={{
          p: 2,
          px: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {/* Progress indicator — sm+ shows statusText or "X of N Complete" counter */}
        {(statusText !== undefined ||
          (completedSteps !== undefined && totalSteps !== undefined)) && (
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Progress
            </Typography>
            <Typography variant="body2" fontWeight={600} color="primary.main">
              {statusText !== undefined
                ? statusText
                : `${completedSteps} of ${totalSteps} Complete`}
            </Typography>
          </Box>
        )}

        {/* Mobile dot indicators — xs only, shown when dotCount provided */}
        {dotCount !== undefined && dotCompleted !== undefined && (
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            {Array.from({ length: dotCount }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: i < dotCompleted ? 'primary.main' : 'grey.300',
                  transition: 'background-color 0.2s ease',
                }}
              />
            ))}
          </Box>
        )}

        {/* Price display */}
        {price !== undefined && (
          <Box sx={{ flex: { xs: 1, sm: 'initial' } }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Total Amount
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                color: 'text.primary',
              }}
            >
              ₹{Math.round(displayPrice).toLocaleString('en-IN')}
            </Typography>
          </Box>
        )}

        {/* Back button */}
        {showBack && onBack && (
          <Button
            variant="outlined"
            size="large"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              height: 56,
              fontWeight: 600,
              fontSize: { xs: 14, sm: 15 },
              borderRadius: 2,
              borderColor: 'grey.300',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'grey.400',
                backgroundColor: 'grey.50',
              },
            }}
          >
            Back
          </Button>
        )}

        {/* CTA Button with optional hint above it */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          {/* Hint text — shown above button when disabled */}
          {hint && disabled && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                textAlign: 'right',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                maxWidth: { xs: 150, sm: 'none' },
              }}
            >
              {hint}
            </Typography>
          )}
          <motion.div
            whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
            whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
            transition={{ duration: 0.1 }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={onClick}
              disabled={disabled || loading}
              endIcon={!loading && <ArrowForwardIcon />}
              sx={{
                minWidth: { xs: 120, sm: 180 },
                height: 56,
                fontWeight: 700,
                fontSize: { xs: 15, sm: 16 },
                borderRadius: 2,
                background:
                  disabled || loading
                    ? 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                    : 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                boxShadow: disabled || loading ? 'none' : '0 4px 12px rgba(99,102,241,0.4)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background:
                    disabled || loading
                      ? 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                      : 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                  boxShadow: disabled || loading ? 'none' : '0 6px 16px rgba(99,102,241,0.5)',
                },
                '&:disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {loading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  Processing...
                </motion.div>
              ) : (
                label
              )}
            </Button>
          </motion.div>
        </Box>
      </Box>
    </Paper>
  );
}
